import React, {useState, useEffect } from 'react';
import { Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import FormGroup from '../forms/FormGroup'
import {useForm, Yup} from '../forms/form'
import { useTranslation } from 'react-i18next';
import { BoolSelect, Select } from '../components';
import FormRowCol from '../forms/FormRowCol';
import Icon from '../Icon';
import { inthertedValue } from './requestConfigUtils';
import styled from "styled-components";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'

const TooltipBadge = styled(FontAwesomeIcon)`
  color: #ff513c;
`

const formGroupWidthProps = {
  xs: 12,
  sm: 12,
  md: 12,
  lg: 8,
  xl: 6
}

const LabelWithTooltip = ({label, tooltip}) => {
  const renderTooltip = (props) => (
    <Tooltip {...props}>
      <div
          dangerouslySetInnerHTML={{__html: tooltip}}
      />
    </Tooltip>
  );

  return(<>
    {label} <OverlayTrigger
      placement="bottom"
      delay={{ show: 250, hide: 400 }}
      overlay={renderTooltip}
    >
      <TooltipBadge icon={faInfoCircle}></TooltipBadge>
    </OverlayTrigger>
  </>)
}

const TooltipExemple = () => {
  const renderTooltip = (props) => (
    <Tooltip id="button-tooltip" {...props}>
      Simple tooltip <br />
      SImple
    </Tooltip>
  );

  return (
    <OverlayTrigger
      placement="bottom"
      delay={{ show: 250, hide: 400 }}
      overlay={renderTooltip}
    >
      <Button variant="success">Hover me to see</Button>
    </OverlayTrigger>
  );
}

const IdfaRequestConfigForm = ({idfas_request_config={}, inherited_idfas_request_config={}, onSave=f=>f, onHeightChange=f=>f}) => {
  const { t } = useTranslation();
  const [onEdit, setOnEdit] = useState(false)
  const [isAndroid, setIsAndroid] = useState(idfas_request_config.os == "android")
  const [showUsePopup, setShowUsePopup] = useState(inthertedValue(idfas_request_config, inherited_idfas_request_config,"use_popup"))
  const initialValues = {
    app_id: '',
    publisher_id: '',
    ask_if_no_consent: '',
    ask_min_days: '',
    ask_min_launches: '',
    number_max_retry: '',
    popup_only_for_retry: '',
    retry_min_days: '',
    retry_min_days_settings: '',
    retry_min_launches: '',
    retry_min_launches_settings: '',
    use_popup: ''
  };
  const validationSchema = Yup.object().shape({
    app_id: Yup.number().allowEmptyString(),
    publisher_id: Yup.string(),
    ask_if_no_consent: Yup.boolean().allowEmptyString(),
    ask_min_days: Yup.number().allowEmptyString(),
    ask_min_launches: Yup.number().allowEmptyString(),
    number_max_retry: Yup.number().allowEmptyString(),
    popup_only_for_retry: Yup.boolean().allowEmptyString(),
    retry_min_days: Yup.number().allowEmptyString(),
    retry_min_days_settings: Yup.number().allowEmptyString(),
    retry_min_launches: Yup.number().allowEmptyString(),
    retry_min_launches_settings: Yup.number().allowEmptyString(),
    use_popup: Yup.boolean().allowEmptyString(),
  })

  const { formData, setFormData, getInputProps, formProps, submitButtonProps } = useForm({
    defaultObject: idfas_request_config,
    initialValues: initialValues,
    validationSchema: validationSchema,
    disabled: !onEdit,
    onSubmit: (data) => {
      onSave(data).then(()=>{
        setOnEdit(false)
      })
    },
  });

  useEffect(() => {
    setShowUsePopup(inthertedValue(formData, inherited_idfas_request_config,"use_popup"))
  }, [formData])

  const cleanData = (obj) => (Object.keys(obj).reduce((accumulator, key) => {
    return {...accumulator, [key]: (accumulator[key] == null) ? '' : accumulator[key]};
  }, obj));

  useEffect(() => {
    setFormData(cleanData(idfas_request_config))
    setIsAndroid(idfas_request_config.os == "android")
  }, [idfas_request_config]);

  useEffect(() => {
    onHeightChange()
  }, [showUsePopup])

  const onCancel = () => {
    setFormData(cleanData(idfas_request_config))
    setOnEdit(false)
  }

  return (
    <Form {...formProps}>
      <FormGroup
        {...formGroupWidthProps}
        label={(<LabelWithTooltip label={t("idfaRequestConfig.askMinDays.title")} tooltip={t("idfaRequestConfig.askMinDays.tooltip")}/>)}
        placeholder={`${inherited_idfas_request_config.ask_min_days} (${t('idfaRequestConfig.inherited')})`}
        input="number"
        {...getInputProps("ask_min_days")}/>
      <FormGroup
        {...formGroupWidthProps}
        label={(<LabelWithTooltip label={t("idfaRequestConfig.askMinLaunches.title")} tooltip={t("idfaRequestConfig.askMinLaunches.tooltip")}/>)}
        placeholder={`${inherited_idfas_request_config.ask_min_launches} (${t('idfaRequestConfig.inherited')})`}
        input="number"
        {...getInputProps("ask_min_launches")}/>
      <FormGroup
        label={(<LabelWithTooltip label={t("idfaRequestConfig.askIfNoConsent.title")} tooltip={t("idfaRequestConfig.askIfNoConsent.tooltip")}/>)}
        placeholder={`${inherited_idfas_request_config.ask_if_no_consent ? t("yes") : t("no") } (${t('idfaRequestConfig.inherited')})`}
        {...getInputProps("ask_if_no_consent")}>
          <BoolSelect isClearable={true}/>
      </FormGroup>
      { !isAndroid && <>
        <FormGroup
          label={(<LabelWithTooltip label={t("idfaRequestConfig.usePopup.title")} tooltip={t("idfaRequestConfig.usePopup.tooltip")}/>)}
          placeholder={`${inherited_idfas_request_config.use_popup ? t("yes") : t("no") } (${t('idfaRequestConfig.inherited')})`}
          {...getInputProps("use_popup")}>
          <BoolSelect isClearable={true}/>
        </FormGroup>
          { showUsePopup && <>
            <FormGroup
              as={FormRowCol}
              xs={{ span: 11, offset: 1 }}
              label={(<LabelWithTooltip label={t("idfaRequestConfig.popupOnlyForRetry.title")} tooltip={t("idfaRequestConfig.popupOnlyForRetry.tooltip")}/>)}
              placeholder={`${inherited_idfas_request_config.popup_only_for_retry ? t("yes") : t("no") } (${t('idfaRequestConfig.inherited')})`}
              {...getInputProps("popup_only_for_retry")}>
              <BoolSelect isClearable={true}/>
            </FormGroup>
            <FormGroup
              as={FormRowCol}
              xs={{ span: 11, offset: 1 }}
              label={(<LabelWithTooltip label={t("idfaRequestConfig.numberMaxRetry.title")} tooltip={t("idfaRequestConfig.numberMaxRetry.tooltip")}/>)}
              placeholder={`${inherited_idfas_request_config.number_max_retry} (${t('idfaRequestConfig.inherited')})`}
              input="number"
              {...getInputProps("number_max_retry")}/>
            <FormGroup
              as={FormRowCol}
              xs={{ span: 11, offset: 1 }}
              label={(<LabelWithTooltip label={t("idfaRequestConfig.retryMinDays.title")} tooltip={t("idfaRequestConfig.retryMinDays.tooltip")}/>)}
              placeholder={`${inherited_idfas_request_config.retry_min_days} (${t('idfaRequestConfig.inherited')})`}
              input="number"
              {...getInputProps("retry_min_days")}/>
            <FormGroup
              as={FormRowCol}
              xs={{ span: 11, offset: 1 }}
              label={(<LabelWithTooltip label={t("idfaRequestConfig.retryMinDaysSettings.title")} tooltip={t("idfaRequestConfig.retryMinDaysSettings.tooltip")}/>)}
              placeholder={`${inherited_idfas_request_config.retry_min_days_settings} (${t('idfaRequestConfig.inherited')})`}
              input="number"
              {...getInputProps("retry_min_days_settings")}/>
            <FormGroup
              as={FormRowCol}
              xs={{ span: 11, offset: 1 }}
              label={(<LabelWithTooltip label={t("idfaRequestConfig.retryMinLaunches.title")} tooltip={t("idfaRequestConfig.retryMinLaunches.tooltip")}/>)}
              placeholder={`${inherited_idfas_request_config.retry_min_launches} (${t('idfaRequestConfig.inherited')})`}
              input="number"
              {...getInputProps("retry_min_launches")}/>
            <FormGroup
              as={FormRowCol}
              xs={{ span: 11, offset: 1 }}
              label={(<LabelWithTooltip label={t("idfaRequestConfig.retryMinLaunchesSettings.title")} tooltip={t("idfaRequestConfig.retryMinLaunchesSettings.tooltip")}/>)}
              placeholder={`${inherited_idfas_request_config.retry_min_launches_settings} (${t('idfaRequestConfig.inherited')})`}
              input="number"
              {...getInputProps("retry_min_launches_settings")}/>
            </>
          }
      </>
      }
      { onEdit && <>
          <Button key="submit" type="submit" className="pull-right" variant="primary" {...submitButtonProps}>
            <Icon icon='save' text={t("save")} />
          </Button>
          <Button variant="secondary" className="pull-right" onClick={onCancel}>
            <Icon icon='cancel' text={t("cancel")}/>
          </Button>
        </> }
      { !onEdit && <>
        <Button variant="primary" className="pull-right" onClick={()=>(setOnEdit(true))}>
          <Icon icon='edit' text={t("edit")} />
        </Button>
      </> }
    </Form>
  )
}


export default IdfaRequestConfigForm